import React, { Fragment, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { gql } from '@apollo/client';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import { useUserRole } from '../../../hooks/useUserRole';
import { useAuth } from '../../../context/AuthContext';
import Header from '../../../components/Header';
import Appfooter from '../../../components/Appfooter';
import Load from '../../../components/Load';
import LocalizacaoMapaEditor from '../../../components/local/LocalizacaoMapaEditor';
import {
  MEUS_LOCAIS,
  LISTAR_CIDADES_GQL,
  CRIAR_LOCAL,
  ATUALIZAR_LOCAL,
  DELETAR_LOCAL,
  QUADRAS_POR_LOCAL_ADMIN,
  DELETAR_QUADRA,
  ORGANIZADORES_LOCAL,
  ADICIONAR_ORGANIZADOR_LOCAL,
  REMOVER_ORGANIZADOR_LOCAL,
  CRIAR_BLOQUEIO,
  DELETAR_BLOQUEIO,
  BLOQUEIOS_POR_LOCAL_E_DATA,
  BLOQUEIOS_DIAS_DO_MES,
  BUSCAR_USUARIOS,
  gerarHorarios,
  TOGGLE_ALUGUEL_LOCAL,
} from '../../../services/quadraAdminService';
import { TIPO_PISO_LABEL } from '../../../services/quadraService';
import { buscarEnderecoPorCep } from '../../../services/cepService';
import { buscarLigasPorNome } from '../../../services/ligaService';
import {
  buscarLigasDoLocal,
  buscarHistoricoDesconto,
  vincularLigaAoLocal,
  desvincularLigaDoLocal,
  salvarDescontoAluno,
  type LigaLocalItem,
  type DescontoLog,
} from '../../../services/ligaLocalService';

const FORM_VAZIO = {
  nome: '', descricao: '', urlLocalizacao: '',
  cep: '', logradouro: '', numero: '', bairro: '', complemento: '', cidadeId: '',
  aceitaPix: true, aceitaCartao: true,
  ligaRecebedoraId: '', ligaRecebedoraNome: '',
};

// ── LigaRecebedoraInput (autocomplete de liga dona financeira do local) ─────────

const LigaRecebedoraInput: React.FC<{
  value: string;
  nomeSelecionado: string;
  onSelecionar: (ligaId: string, ligaNome: string) => void;
  onLimpar: () => void;
}> = ({ value, nomeSelecionado, onSelecionar, onLimpar }) => {
  const [busca, setBusca] = useState('');
  const [opcoes, setOpcoes] = useState<Array<{ id: string; nome: string }>>([]);
  const [buscando, setBuscando] = useState(false);

  useEffect(() => {
    if (busca.trim().length < 2) { setOpcoes([]); return; }
    let ativo = true;
    setBuscando(true);
    const t = setTimeout(async () => {
      try {
        const conexao = await buscarLigasPorNome(busca.trim(), 0, 10);
        if (ativo) setOpcoes((conexao?.items ?? []).map((l: any) => ({ id: l.id, nome: l.nome })));
      } catch {
        if (ativo) setOpcoes([]);
      } finally {
        if (ativo) setBuscando(false);
      }
    }, 350);
    return () => { ativo = false; clearTimeout(t); };
  }, [busca]);

  if (value) {
    return (
      <div className="d-flex align-items-center gap-2 border rounded-xl px-3 py-2 bg-light">
        <span className="font-xssss flex-grow-1"><strong>{nomeSelecionado || 'Liga selecionada'}</strong></span>
        <button type="button" className="btn-close btn-close-sm" onClick={() => { onLimpar(); setBusca(''); }} />
      </div>
    );
  }

  return (
    <div className="position-relative">
      <input
        className="form-control rounded-xl font-xsss"
        value={busca}
        onChange={e => setBusca(e.target.value)}
        placeholder="Buscar liga por nome..."
        autoComplete="off"
      />
      {buscando && <small className="text-grey-500 font-xssss">Buscando...</small>}
      {!buscando && busca.trim().length >= 2 && opcoes.length === 0 && (
        <small className="text-grey-500 font-xssss">Nenhuma liga encontrada.</small>
      )}
      {opcoes.length > 0 && busca.trim().length >= 2 && (
        <div className="border rounded-xl shadow-sm bg-white position-absolute w-100" style={{ zIndex: 100, top: '100%', maxHeight: 180, overflowY: 'auto' }}>
          {opcoes.map(o => (
            <div key={o.id} className="px-3 py-2 font-xssss" style={{ cursor: 'pointer' }} onMouseDown={() => { onSelecionar(o.id, o.nome); setBusca(''); }}>
              <strong>{o.nome}</strong>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── ModalAdmins ───────────────────────────────────────────────────────────────

const ModalAdmins: React.FC<{ local: any; onFechar: () => void }> = ({ local, onFechar }) => {
  const [buscaUsuario, setBuscaUsuario] = useState('');
  const [erro, setErro] = useState('');
  const { data, loading, refetch } = useQuery(ORGANIZADORES_LOCAL, { variables: { localId: local.id }, fetchPolicy: 'network-only' });
  const [buscarUsuarios, { data: usuariosData, loading: buscando }] = useLazyQuery(BUSCAR_USUARIOS, { fetchPolicy: 'network-only' });
  const [adicionarAdmin] = useMutation(ADICIONAR_ORGANIZADOR_LOCAL);
  const [removerAdmin] = useMutation(REMOVER_ORGANIZADOR_LOCAL);
  const admins = data?.organizadoresLocal ?? [];

  const handleBusca = (v: string) => { setBuscaUsuario(v); if (v.length >= 2) buscarUsuarios({ variables: { nome: v } }); };
  const handleAdicionar = async (usuarioId: string) => {
    setErro(''); setBuscaUsuario('');
    try { await adicionarAdmin({ variables: { localId: local.id, usuarioId } }); refetch(); }
    catch (err: any) { setErro(err?.graphQLErrors?.[0]?.message ?? err.message); }
  };
  const handleRemover = async (usuarioId: string, nome: string) => {
    if (!window.confirm(`Remover "${nome}" como administrador?`)) return;
    setErro('');
    try { await removerAdmin({ variables: { localId: local.id, usuarioId } }); refetch(); }
    catch (err: any) { setErro(err?.graphQLErrors?.[0]?.message ?? err.message); }
  };

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', position: 'fixed', inset: 0, zIndex: 9999, overflowY: 'auto' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 480 }}>
        <div className="modal-content rounded-xxl border-0 shadow-lg p-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div><h5 className="fw-700 font-xss mb-0">Administradores</h5><span className="font-xssss text-grey-500">{local.nome}</span></div>
            <button className="btn-close" onClick={onFechar} />
          </div>
          <div className="mb-3 position-relative">
            <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Adicionar administrador</label>
            <input className="form-control rounded-xl font-xsss" value={buscaUsuario} onChange={e => handleBusca(e.target.value)} placeholder="Buscar usuário por nome..." autoComplete="off" />
            {buscando && <small className="text-grey-500 font-xssss">Buscando...</small>}
            {!buscando && buscaUsuario.length >= 2 && (usuariosData?.usuariosPorNome?.length ?? 0) === 0 && <small className="text-grey-500 font-xssss">Nenhum usuário encontrado.</small>}
            {(usuariosData?.usuariosPorNome?.length ?? 0) > 0 && buscaUsuario.length >= 2 && (
              <div className="border rounded-xl shadow-sm bg-white position-absolute w-100" style={{ zIndex: 100, top: '100%', maxHeight: 180, overflowY: 'auto' }}>
                {usuariosData.usuariosPorNome.map((u: any) => (
                  <div key={u.id} className="px-3 py-2 font-xssss" style={{ cursor: 'pointer' }} onMouseDown={() => handleAdicionar(u.id)}>
                    <strong>{u.nome}</strong> <span className="text-grey-500">{u.email}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {erro && <div className="alert alert-danger font-xssss py-2 mb-2">{erro}</div>}
          <div>
            <p className="font-xssss fw-700 text-grey-700 mb-2">Administradores atuais</p>
            {loading && <Load />}
            {!loading && admins.length === 0 && <p className="text-grey-500 font-xssss text-center py-2">Nenhum administrador cadastrado.</p>}
            {admins.map((a: any) => (
              <div key={a.id} className="d-flex align-items-center justify-content-between py-2 border-bottom">
                <div><span className="font-xssss fw-600 d-block">{a.usuario.nome}</span><span className="font-xssss text-grey-500">{a.usuario.email}</span></div>
                <button className="btn btn-outline-danger rounded-xl font-xssss px-2 py-1" onClick={() => handleRemover(a.usuario.id, a.usuario.nome)}><i className="feather-x"></i></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── ModalBloqueioLote ─────────────────────────────────────────────────────────

const ModalBloqueioLote: React.FC<{ local: any; onFechar: () => void }> = ({ local, onFechar }) => {
  const [aba, setAba] = useState<'consulta' | 'novo'>('consulta');
  const [data, setData] = useState(new Date().toISOString().split('T')[0]);
  const [quadrasSelecionadas, setQuadrasSelecionadas] = useState<string[]>([]);
  const [horaInicio, setHoraInicio] = useState('08:00');
  const [horaFim, setHoraFim] = useState('12:00');
  const [motivo, setMotivo] = useState('');
  const [forcar, setForcar] = useState(false);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const HORARIOS = gerarHorarios();

  const { data: quadrasData, loading: loadingQuadras } = useQuery(QUADRAS_POR_LOCAL_ADMIN, { variables: { localId: local.id }, fetchPolicy: 'cache-and-network' });
  const { data: bloqueiosData, loading: loadingBloqueios, refetch: refetchBloqueios } = useQuery(BLOQUEIOS_POR_LOCAL_E_DATA, { variables: { localId: local.id, data }, fetchPolicy: 'network-only' });
  const mesAtual = parseInt(data.split('-')[1]);
  const anoAtual = parseInt(data.split('-')[0]);
  const { data: diasData, refetch: refetchDias } = useQuery(BLOQUEIOS_DIAS_DO_MES, { variables: { localId: local.id, mes: mesAtual, ano: anoAtual }, fetchPolicy: 'network-only' });
  const diasComBloqueio: number[] = (diasData?.bloqueiosDiasDoMes ?? []).map((d: string) => parseInt(d.split('-')[2] || d));
  const diaAtual = parseInt(data.split('-')[2]);
  const [criarBloqueio] = useMutation(CRIAR_BLOQUEIO);
  const [deletarBloqueio] = useMutation(DELETAR_BLOQUEIO);
  const quadras: any[] = (quadrasData?.quadrasPorLocalAdmin ?? []).filter((q: any) => q.ativa);
  const bloqueiosPorQuadra: { quadra: any; bloqueios: any[] }[] = bloqueiosData?.bloqueiosPorLocalEData ?? [];

  const toggleQuadra = (id: string) => setQuadrasSelecionadas(prev => prev.includes(id) ? prev.filter(q => q !== id) : [...prev, id]);
  const toggleTodas = () => setQuadrasSelecionadas(quadrasSelecionadas.length === quadras.length ? [] : quadras.map((q: any) => q.id));

  const handleDesbloquear = async (bloqueioId: string) => {
    if (!window.confirm('Desbloquear este horário?')) return;
    try { await deletarBloqueio({ variables: { id: bloqueioId } }); refetchBloqueios(); refetchDias(); }
    catch (err: any) { alert(err?.graphQLErrors?.[0]?.message ?? err.message); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (quadrasSelecionadas.length === 0) { setErro('Selecione ao menos uma quadra.'); return; }
    if (!motivo.trim()) { setErro('Informe o motivo do bloqueio.'); return; }
    if (horaFim <= horaInicio) { setErro('Horário de fim deve ser depois do início.'); return; }
    setErro(''); setSalvando(true);
    try {
      await Promise.all(quadrasSelecionadas.map(quadraId => criarBloqueio({ variables: { input: { quadraId, dataBloqueio: data, horaInicio, horaFim, motivo, forcar } } })));
      setSucesso(true); refetchBloqueios(); refetchDias();
    } catch (err: any) { setErro(err?.graphQLErrors?.[0]?.message ?? err.message); }
    finally { setSalvando(false); }
  };

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', position: 'fixed', inset: 0, zIndex: 9999, overflowY: 'auto' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 540 }}>
        <div className="modal-content rounded-xxl border-0 shadow-lg p-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div><h5 className="fw-700 font-xss mb-0">Bloqueios de Horários</h5><span className="font-xssss text-grey-500">{local.nome}</span></div>
            <button className="btn-close" onClick={onFechar} />
          </div>
          <div className="mb-3">
            <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Data</label>
            <div className="d-flex align-items-center gap-2">
              <button type="button" className="btn btn-light rounded-xl px-2" style={{ height: 38 }} onClick={() => { const d = new Date(data + 'T12:00:00'); d.setDate(d.getDate() - 1); setData(d.toISOString().split('T')[0]); setSucesso(false); }}><i className="feather-chevron-left"></i></button>
              <input type="date" className="form-control rounded-xl font-xsss" value={data} onChange={e => { setData(e.target.value); setSucesso(false); }} style={{ width: 160, height: 38 }} />
              <button type="button" className="btn btn-light rounded-xl px-2" style={{ height: 38 }} onClick={() => { const d = new Date(data + 'T12:00:00'); d.setDate(d.getDate() + 1); setData(d.toISOString().split('T')[0]); setSucesso(false); }}><i className="feather-chevron-right"></i></button>
              <span className="font-xssss text-grey-500">{['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'][new Date(data + 'T12:00:00').getDay()]}</span>
              {diasComBloqueio.includes(diaAtual) && <span className="badge bg-warning text-dark font-xssss rounded-xl px-2"><i className="feather-lock me-1"></i>com bloqueio</span>}
            </div>
            {diasComBloqueio.length > 0 && (
              <div className="mt-2 font-xssss text-warning fw-600">
                <i className="feather-lock me-1"></i>Dias com bloqueio em {new Date(data + 'T12:00:00').toLocaleDateString('pt-BR', { month: 'long' })}: {diasComBloqueio.slice().sort((a, b) => a - b).join(', ')}
              </div>
            )}
          </div>
          <div className="d-flex gap-2 mb-3">
            <button type="button" className={`btn rounded-xl font-xssss fw-600 px-3 py-1 ${aba === 'consulta' ? 'bg-primary-gradiant text-white' : 'btn-light'}`} onClick={() => { setAba('consulta'); setSucesso(false); setErro(''); }}><i className="feather-list me-1"></i>Bloqueios ativos</button>
            <button type="button" className={`btn rounded-xl font-xssss fw-600 px-3 py-1 ${aba === 'novo' ? 'btn-warning' : 'btn-light'}`} onClick={() => { setAba('novo'); setSucesso(false); setErro(''); }}><i className="feather-lock me-1"></i>Novo bloqueio</button>
          </div>
          {aba === 'consulta' && (
            <div>
              {loadingBloqueios && <Load />}
              {!loadingBloqueios && bloqueiosPorQuadra.length === 0 && <div className="text-center py-4"><i className="feather-unlock text-grey-300" style={{ fontSize: 36 }}></i><p className="font-xssss text-grey-500 mt-2 mb-0">Nenhum bloqueio para esta data.</p></div>}
              {bloqueiosPorQuadra.map(({ quadra, bloqueios }) => (
                <div key={quadra.id} className="mb-3">
                  <p className="font-xssss fw-700 text-grey-700 mb-1"><i className="bi bi-grid-3x3 me-1 text-primary"></i>{quadra.nome}</p>
                  {bloqueios.map((b: any) => (
                    <div key={b.id} className="d-flex align-items-center justify-content-between p-2 mb-1 rounded-xl" style={{ background: '#fff8e1' }}>
                      <span className="font-xssss fw-600">{b.horaInicio} – {b.horaFim}</span>
                      {b.motivo && <span className="font-xssss text-grey-500 mx-2 flex-grow-1">{b.motivo}</span>}
                      <button className="btn btn-outline-warning rounded-xl font-xssss px-2 py-1" onClick={() => handleDesbloquear(b.id)}><i className="feather-unlock me-1"></i>Desbloquear</button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          {aba === 'novo' && (
            sucesso ? (
              <div className="text-center py-4">
                <i className="feather-check-circle text-success" style={{ fontSize: 48 }}></i>
                <p className="fw-600 font-xss mt-3 mb-1">Bloqueios criados com sucesso!</p>
                <p className="font-xssss text-grey-500 mb-3">{quadrasSelecionadas.length} quadra(s) em {new Date(data + 'T12:00:00').toLocaleDateString('pt-BR')} das {horaInicio} às {horaFim}</p>
                <div className="d-flex gap-2 justify-content-center">
                  <button className="btn btn-light rounded-xl font-xssss fw-600 px-3" onClick={() => { setSucesso(false); setAba('consulta'); }}>Ver bloqueios</button>
                  <button className="btn bg-primary-gradiant text-white rounded-xl font-xssss fw-600 px-3" onClick={onFechar}>Fechar</button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <label className="font-xssss fw-600 text-grey-600">Quadras</label>
                    <button type="button" className="btn btn-link font-xssss p-0" onClick={toggleTodas}>{quadrasSelecionadas.length === quadras.length ? 'Desmarcar todas' : 'Selecionar todas'}</button>
                  </div>
                  {loadingQuadras && <Load />}
                  {quadras.map((q: any) => (
                    <div key={q.id} className="form-check mb-1">
                      <input type="checkbox" className="form-check-input" id={`q-${q.id}`} checked={quadrasSelecionadas.includes(q.id)} onChange={() => toggleQuadra(q.id)} />
                      <label className="form-check-label font-xssss" htmlFor={`q-${q.id}`}>{q.nome}</label>
                    </div>
                  ))}
                </div>
                <div className="row mb-2">
                  <div className="col-6"><label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Início</label><select className="form-select rounded-xl font-xsss" value={horaInicio} onChange={e => setHoraInicio(e.target.value)}>{HORARIOS.map(h => <option key={h} value={h}>{h}</option>)}</select></div>
                  <div className="col-6"><label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Fim</label><select className="form-select rounded-xl font-xsss" value={horaFim} onChange={e => setHoraFim(e.target.value)}>{HORARIOS.map(h => <option key={h} value={h}>{h}</option>)}</select></div>
                </div>
                <div className="mb-2"><label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Motivo *</label><input className="form-control rounded-xl font-xsss" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ex: Manutenção, evento..." /></div>
                <div className="form-check mb-3"><input type="checkbox" className="form-check-input" id="forcarLote" checked={forcar} onChange={e => setForcar(e.target.checked)} /><label className="form-check-label font-xssss" htmlFor="forcarLote">Forçar (mesmo com agendamentos conflitantes)</label></div>
                {erro && <div className="alert alert-danger font-xssss py-2 mb-2">{erro}</div>}
                <div className="d-flex gap-2">
                  <button type="submit" className="btn btn-warning rounded-xl font-xssss fw-600 px-3" disabled={salvando}><i className="feather-lock me-1"></i>{salvando ? 'Bloqueando...' : `Bloquear${quadrasSelecionadas.length > 0 ? ` (${quadrasSelecionadas.length})` : ''}`}</button>
                  <button type="button" className="btn btn-light rounded-xl font-xssss fw-600 px-3" onClick={onFechar}>Cancelar</button>
                </div>
              </form>
            )
          )}
        </div>
      </div>
    </div>
  );
};

// ── LigaSearchInput ───────────────────────────────────────────────────────────

const LigaSearchInput: React.FC<{
  localId: string;
  ligasVinculadas: LigaLocalItem[];
  onVincular: (ligaId: string, ligaNome: string) => void;
}> = ({ ligasVinculadas, onVincular }) => {
  const auth = useAuth();
  const user = auth?.user as any;

  // Ligas em que o usuário é organizador (já vêm no token/login)
  const minhasLigas: { id: string; nome: string; imagemUrl?: string | null }[] = (
    user?.organizadores ?? []
  )
    .filter((o: any) => o.liga != null)
    .map((o: any) => o.liga)
    .filter((liga: any, idx: number, arr: any[]) => arr.findIndex((l: any) => l.id === liga.id) === idx);

  const disponiveis = minhasLigas.filter(l => !ligasVinculadas.some(v => v.liga.id === l.id));

  if (disponiveis.length === 0) {
    return <p className="font-xssss text-grey-400 mb-2">Nenhuma liga disponível para vincular.</p>;
  }

  return (
    <div className="mb-2">
      <select
        className="form-select rounded-xl font-xsss"
        defaultValue=""
        onChange={e => {
          const id = e.target.value;
          if (!id) return;
          const liga = disponiveis.find(l => l.id === id);
          if (liga) onVincular(liga.id, liga.nome);
          e.target.value = '';
        }}
      >
        <option value="">Selecionar liga para vincular...</option>
        {disponiveis.map(l => (
          <option key={l.id} value={l.id}>{l.nome}</option>
        ))}
      </select>
    </div>
  );
};

// ── ModalDescontoAluno ────────────────────────────────────────────────────────

const ModalDescontoAluno: React.FC<{ local: any; onFechar: () => void }> = ({ local, onFechar }) => {
  const [percentual, setPercentual] = useState<string>(local.percentualDescontoAluno != null ? String(local.percentualDescontoAluno) : '');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState(false);
  const [ligas, setLigas] = useState<LigaLocalItem[]>([]);
  const [historico, setHistorico] = useState<DescontoLog[]>([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    setCarregando(true);
    Promise.all([buscarLigasDoLocal(local.id), buscarHistoricoDesconto(local.id)])
      .then(([l, h]) => { setLigas(l); setHistorico(h); })
      .finally(() => setCarregando(false));
  }, [local.id]);

  const handleSalvar = async () => {
    const pct = percentual === '' ? null : parseFloat(percentual);
    if (pct !== null && (isNaN(pct) || pct < 0 || pct > 100)) { setErro('Percentual deve ser entre 0 e 100.'); return; }
    setSalvando(true); setErro(''); setSucesso(false);
    const ok = await salvarDescontoAluno(local.id, pct);
    setSalvando(false);
    if (ok) {
      setSucesso(true);
      const novoLog = await buscarHistoricoDesconto(local.id);
      setHistorico(novoLog);
      setTimeout(() => setSucesso(false), 3000);
    } else {
      setErro('Erro ao salvar desconto.');
    }
  };

  const handleVincular = async (ligaId: string) => {
    const item = await vincularLigaAoLocal(local.id, ligaId);
    if (item) setLigas(prev => [...prev, item]);
  };

  const handleDesvincular = async (ligaId: string) => {
    if (!window.confirm('Remover esta liga do desconto?')) return;
    const ok = await desvincularLigaDoLocal(local.id, ligaId);
    if (ok) setLigas(prev => prev.filter(l => l.liga.id !== ligaId));
  };

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', position: 'fixed', inset: 0, zIndex: 9999, overflowY: 'auto' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 520 }}>
        <div className="modal-content rounded-xxl border-0 shadow-lg p-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div><h5 className="fw-700 font-xss mb-0">Desconto para Alunos</h5><span className="font-xssss text-grey-500">{local.nome}</span></div>
            <button className="btn-close" onClick={onFechar} />
          </div>

          <div className="mb-3 p-3 bg-greylight rounded-xl">
            <label className="font-xssss fw-600 text-grey-600 mb-2 d-block">Percentual de desconto para alunos (%)</label>
            <div className="d-flex align-items-center gap-2">
              <input type="number" min="0" max="100" step="0.5" className="form-control rounded-xl font-xsss" style={{ width: 120 }} placeholder="Ex: 15" value={percentual} onChange={e => { setPercentual(e.target.value); setSucesso(false); }} />
              <span className="font-xssss text-grey-500">%</span>
              <button className="btn bg-primary-gradiant text-white rounded-xl font-xssss fw-600 px-3" onClick={handleSalvar} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</button>
              {percentual !== '' && <button className="btn btn-outline-secondary rounded-xl font-xssss px-2" data-tooltip="Remover desconto" onClick={() => setPercentual('')}><i className="feather-x"></i></button>}
            </div>
            {erro && <div className="alert alert-danger font-xssss py-1 mt-2 mb-0">{erro}</div>}
            {sucesso && <div className="alert alert-success font-xssss py-1 mt-2 mb-0">Desconto salvo com sucesso!</div>}
          </div>

          <div className="mb-3">
            <p className="font-xssss fw-700 text-grey-700 mb-1">Ligas com acesso ao desconto</p>
            <p className="font-xssss text-grey-500 mb-2">Apenas alunos de ligas vinculadas recebem o desconto neste local.</p>
            <LigaSearchInput localId={local.id} ligasVinculadas={ligas} onVincular={handleVincular} />
            {carregando && <Load />}
            {!carregando && ligas.length === 0 && <p className="font-xssss text-grey-400 text-center py-2">Nenhuma liga vinculada.</p>}
            {ligas.map(item => (
              <div key={item.id} className="d-flex align-items-center justify-content-between py-2 border-bottom">
                <div className="d-flex align-items-center gap-2">
                  {item.liga.imagemUrl && <img src={item.liga.imagemUrl} alt={item.liga.nome} className="rounded-circle" style={{ width: 28, height: 28, objectFit: 'cover' }} />}
                  <span className="font-xssss fw-600">{item.liga.nome}</span>
                </div>
                <button className="btn btn-outline-danger rounded-xl font-xssss px-2 py-1" onClick={() => handleDesvincular(item.liga.id)}><i className="feather-x"></i></button>
              </div>
            ))}
          </div>

          {historico.length > 0 && (
            <div>
              <p className="font-xssss fw-700 text-grey-700 mb-2">Histórico de alterações</p>
              <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                {historico.map(log => (
                  <div key={log.id} className="d-flex align-items-center justify-content-between py-1 border-bottom font-xssss">
                    <span className="text-grey-600">{log.percentualAnterior != null ? `${log.percentualAnterior}%` : '—'}{' → '}{log.percentualNovo != null ? `${log.percentualNovo}%` : 'removido'}</span>
                    <span className="text-grey-500">{log.usuario.nome}</span>
                    <span className="text-grey-400">{new Date(log.alteradoEm).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── LocalCard ─────────────────────────────────────────────────────────────────

const LocalCard: React.FC<{
  local: any;
  onEditar: (local: any) => void;
  onDeletarLocal: (local: any) => void;
  onAdmins: (local: any) => void;
  onBloqueio: (local: any) => void;
  onDesconto: (local: any) => void;
  isAdmin: boolean;
}> = ({ local, onEditar, onDeletarLocal, onAdmins, onBloqueio, onDesconto, isAdmin }) => {
  const navigate = useNavigate();
  const [expandido, setExpandido] = useState(false);
  const [toggleAluguel, { loading: toggling }] = useMutation(TOGGLE_ALUGUEL_LOCAL);

  const handleToggleAluguel = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const acao = local.aluguelHabilitado ? 'desabilitar' : 'habilitar';
    if (!window.confirm(`Deseja mesmo ${acao} o aluguel do local "${local.nome}"?`)) return;
    try {
      await toggleAluguel({ variables: { id: local.id, aluguelHabilitado: !local.aluguelHabilitado }, optimisticResponse: { toggleAluguelLocal: { __typename: 'Local', id: local.id, aluguelHabilitado: !local.aluguelHabilitado } } });
    } catch {}
  };

  const { data, loading, refetch } = useQuery(QUADRAS_POR_LOCAL_ADMIN, { variables: { localId: local.id }, skip: !expandido, fetchPolicy: 'cache-and-network' });
  const [deletar, { loading: deletando }] = useMutation(DELETAR_QUADRA);
  const quadras = data?.quadrasPorLocalAdmin ?? [];

  const handleDeletar = async (id: string, nome: string) => {
    if (!window.confirm(`Deletar a quadra "${nome}"? Esta ação não pode ser desfeita.`)) return;
    await deletar({ variables: { id } });
    refetch();
  };

  return (
    <div className="card shadow-xss rounded-xxl border-0 mb-3">
      <div className="p-4 d-flex align-items-center justify-content-between" style={{ cursor: 'pointer' }} onClick={() => setExpandido(v => !v)}>
        <div className="d-flex align-items-center gap-3">
          <span className="btn-round-lg bg-primary-gradiant d-flex align-items-center justify-content-center" style={{ minWidth: 48, minHeight: 48 }}>
            <i className="bi bi-buildings text-white font-md"></i>
          </span>
          <div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <h4 className="fw-700 font-xss mb-0 text-grey-900">{local.nome}</h4>
              {local.ligaRecebedora ? (
                <span className="badge bg-success-subtle text-success font-xssss rounded-xl" title={`Repasse dos aluguéis vai para ${local.ligaRecebedora.nome}`}>
                  <i className="feather-dollar-sign me-1"></i>{local.ligaRecebedora.nome}
                </span>
              ) : local.aluguelHabilitado ? (
                <span className="badge bg-warning text-dark font-xssss rounded-xl" title="Nenhuma liga recebedora definida — o recebimento vai integralmente para a InPlay">
                  <i className="feather-alert-triangle me-1"></i>Recebimento → InPlay
                </span>
              ) : null}
            </div>
            <span className="font-xssss text-grey-500">{local.endereco?.bairro}{local.endereco?.cidade?.nome ? `, ${local.endereco.cidade.nome}` : ''}</span>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button className="btn bg-primary-gradiant text-white rounded-xl font-xssss fw-600 px-3 py-1" onClick={e => { e.stopPropagation(); navigate(`/admin/quadras/nova?localId=${local.id}&localNome=${encodeURIComponent(local.nome)}`); }}><i className="feather-plus me-1"></i> Nova quadra</button>
          <button className="btn btn-outline-primary rounded-xl font-xssss fw-600 px-2 py-1" data-tooltip="Agenda geral" onClick={e => { e.stopPropagation(); navigate(`/admin/quadras/agenda-local/${local.id}`); }}><i className="feather-calendar"></i></button>
          <button className="btn btn-light rounded-xl font-xssss fw-600 px-2 py-1" data-tooltip="Desconto para alunos" onClick={e => { e.stopPropagation(); onDesconto(local); }}><i className="bi bi-percent"></i></button>
          <button className="btn btn-outline-warning rounded-xl font-xssss fw-600 px-2 py-1" data-tooltip="Bloquear horários" onClick={e => { e.stopPropagation(); onBloqueio(local); }}><i className="feather-lock"></i></button>
          <button className="btn btn-light rounded-xl font-xssss fw-600 px-2 py-1" data-tooltip="Administradores" onClick={e => { e.stopPropagation(); onAdmins(local); }}><i className="feather-users"></i></button>
          <button className="btn btn-light rounded-xl font-xssss fw-600 px-2 py-1" data-tooltip="Editar local" onClick={e => { e.stopPropagation(); onEditar(local); }}><i className="feather-edit-2"></i></button>
          <button className="btn btn-outline-danger rounded-xl font-xssss fw-600 px-2 py-1" data-tooltip="Deletar local" onClick={e => { e.stopPropagation(); onDeletarLocal(local); }}><i className="feather-trash-2"></i></button>
          {isAdmin && (
            <button className={`btn rounded-xl font-xssss fw-600 px-2 py-1 ${local.aluguelHabilitado ? 'btn-success' : 'btn-outline-secondary'}`} data-tooltip={local.aluguelHabilitado ? 'Aluguel habilitado — clique para desabilitar' : 'Aluguel desabilitado — clique para habilitar'} onClick={handleToggleAluguel} disabled={toggling}>
              <i className={`feather-${local.aluguelHabilitado ? 'toggle-right' : 'toggle-left'} me-1`}></i>{local.aluguelHabilitado ? 'Aluguel ON' : 'Aluguel OFF'}
            </button>
          )}
          <i className={`feather-chevron-${expandido ? 'up' : 'down'} text-grey-400`}></i>
        </div>
      </div>
      {expandido && (
        <div className="border-top">
          {loading && <div className="p-3"><Load /></div>}
          {!loading && quadras.length === 0 && <p className="text-grey-500 font-xssss text-center py-3">Nenhuma quadra cadastrada neste local.</p>}
          {quadras.map((q: any) => (
            <div key={q.id} className="d-flex align-items-center p-3 border-bottom gap-3">
              <div style={{ width: 56, height: 56, borderRadius: 12, flexShrink: 0, background: q.imagens?.[0]?.url ? `url(${q.imagens?.[0]?.url}) center/cover` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }} />
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <h5 className="fw-700 font-xsss mb-0 text-grey-900">{q.nome}</h5>
                  <span className="badge bg-primary-gradiant text-white font-xssss rounded-xl">{TIPO_PISO_LABEL[q.tipoPiso] ?? q.tipoPiso}</span>
                  {!q.ativa && <span className="badge bg-danger font-xssss rounded-xl">Inativa</span>}
                  {(() => {
                    const p = q.pendenciasConfiguracao;
                    if (!p) return null;
                    return (
                      <>
                        {p.semHorarios && <span className="badge bg-warning text-dark font-xssss rounded-xl"><i className="feather-alert-triangle me-1"></i>Sem horários</span>}
                        {p.semPrecos && <span className="badge bg-warning text-dark font-xssss rounded-xl"><i className="feather-alert-triangle me-1"></i>Sem preços</span>}
                        {!p.semHorarios && !p.semPrecos && p.horariosDescobertosSemPreco && <span className="badge bg-warning text-dark font-xssss rounded-xl"><i className="feather-alert-triangle me-1"></i>Horários sem preço</span>}
                      </>
                    );
                  })()}
                </div>
                {q.descricao && <p className="font-xssss text-grey-500 mb-0">{q.descricao}</p>}
              </div>
              <div className="d-flex gap-2 flex-shrink-0">
                <button className="btn btn-light rounded-xl font-xssss fw-600 px-2 py-1" data-tooltip="Agenda" onClick={() => navigate(`/admin/quadras/${q.id}/agenda`)}><i className="feather-calendar"></i></button>
                <button className="btn btn-light rounded-xl font-xssss fw-600 px-2 py-1" data-tooltip="Horários e Preços" onClick={() => navigate(`/admin/quadras/${q.id}/configurar`)}><i className="feather-settings"></i></button>
                <button className="btn btn-light rounded-xl font-xssss fw-600 px-2 py-1" data-tooltip="Editar" onClick={() => navigate(`/admin/quadras/${q.id}/editar?localId=${local.id}&localNome=${encodeURIComponent(local.nome)}`)}><i className="feather-edit-2"></i></button>
                <button className="btn btn-outline-danger rounded-xl font-xssss fw-600 px-2 py-1" data-tooltip="Deletar" onClick={() => handleDeletar(q.id, q.nome)} disabled={deletando}><i className="feather-trash-2"></i></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── AdminQuadrasDashboard ─────────────────────────────────────────────────────

const AdminQuadrasDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin } = useUserRole();
  const { data, loading, error, refetch } = useQuery(MEUS_LOCAIS, { fetchPolicy: 'cache-and-network' });
  const { data: cidadesData } = useQuery(LISTAR_CIDADES_GQL);
  const [criarLocal, { loading: criando }] = useMutation(CRIAR_LOCAL);
  const [atualizarLocal, { loading: atualizando }] = useMutation(ATUALIZAR_LOCAL);
  const [deletarLocal] = useMutation(DELETAR_LOCAL);

  const todosLocais = data?.meusLocais ?? [];
  const cidades = cidadesData?.cidades ?? [];

  const [modalForm, setModalForm] = useState(false);
  const [localEditando, setLocalEditando] = useState<any | null>(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [erro, setErro] = useState('');
  const [localAdmins, setLocalAdmins] = useState<any | null>(null);
  const [localBloqueio, setLocalBloqueio] = useState<any | null>(null);
  const [localDesconto, setLocalDesconto] = useState<any | null>(null);
  const [filtroAluguel, setFiltroAluguel] = useState<'todos' | 'habilitado' | 'desabilitado'>('habilitado');
  const [filtroCidade, setFiltroCidade] = useState('');
  const [filtroDescricao, setFiltroDescricao] = useState('');
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [cepErro, setCepErro] = useState<string | null>(null);

  const locais = todosLocais.filter((l: any) => {
    if (filtroAluguel === 'habilitado' && !l.aluguelHabilitado) return false;
    if (filtroAluguel === 'desabilitado' && l.aluguelHabilitado) return false;
    if (filtroCidade && l.endereco?.cidade?.id !== filtroCidade) return false;
    if (filtroDescricao && !l.nome?.toLowerCase().includes(filtroDescricao.toLowerCase())) return false;
    return true;
  });

  const abrirNovo = () => { setLocalEditando(null); setForm(FORM_VAZIO); setErro(''); setCepErro(null); setModalForm(true); };
  const abrirEditar = (local: any) => {
    setLocalEditando(local);
    setForm({ nome: local.nome ?? '', descricao: local.descricao ?? '', urlLocalizacao: local.urlLocalizacao ?? '', cep: local.endereco?.cep ?? '', logradouro: local.endereco?.logradouro ?? '', numero: local.endereco?.numero ?? '', bairro: local.endereco?.bairro ?? '', complemento: local.endereco?.complemento ?? '', cidadeId: local.endereco?.cidade?.id ?? '', aceitaPix: local.aceitaPix !== false, aceitaCartao: local.aceitaCartao !== false, ligaRecebedoraId: local.ligaRecebedora?.id ?? '', ligaRecebedoraNome: local.ligaRecebedora?.nome ?? '' });
    setErro(''); setCepErro(null); setModalForm(true);
  };
  const fecharForm = () => { setModalForm(false); setErro(''); setCepErro(null); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setErro('');
    const input: any = { nome: form.nome, descricao: form.descricao || undefined, urlLocalizacao: form.urlLocalizacao || undefined, aceitaPix: form.aceitaPix, aceitaCartao: form.aceitaCartao, endereco: { cep: form.cep || undefined, logradouro: form.logradouro || undefined, numero: form.numero || undefined, bairro: form.bairro || undefined, complemento: form.complemento || undefined, cidadeId: form.cidadeId || undefined } };
    // Liga recebedora só é enviada por ADMIN (único que vê/edita o campo). Para gestor, o
    // campo é omitido do input, então o backend nunca tenta alterá-lo.
    if (isAdmin) input.ligaRecebedoraId = form.ligaRecebedoraId;
    if (localEditando?.endereco?.id) input.endereco.id = localEditando.endereco.id;
    try {
      if (localEditando) { await atualizarLocal({ variables: { id: localEditando.id, input } }); }
      else { await criarLocal({ variables: { input } }); }
      fecharForm(); refetch();
    } catch (err: any) { setErro(err?.graphQLErrors?.[0]?.message ?? err.message); }
  };

  const handleDeletarLocal = async (local: any) => {
    if (!window.confirm(`Deletar o local "${local.nome}"? Esta ação não pode ser desfeita.`)) return;
    try { await deletarLocal({ variables: { id: local.id } }); refetch(); }
    catch (err: any) { alert(err?.graphQLErrors?.[0]?.message ?? err.message); }
  };

  const f = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setForm(v => ({ ...v, [field]: e.target.value }));

  const buscarCep = async () => {
    setCepErro(null);
    const cepValue = form.cep;
    if (!cepValue) { setCepErro('Informe o CEP'); return; }
    const cepLimpo = cepValue.replace(/\D/g, '');
    if (cepLimpo.length !== 8) { setCepErro('CEP deve ter 8 dígitos'); return; }
    setBuscandoCep(true);
    try {
      const d = await buscarEnderecoPorCep(cepValue);
      setForm(v => ({
        ...v,
        logradouro: d.logradouro || v.logradouro,
        bairro: d.bairro || v.bairro,
        complemento: d.complemento || v.complemento,
      }));
      // Tenta selecionar cidade automaticamente pelo nome
      const cidadeMatch = cidades.find((c: any) =>
        c.nome.toLowerCase() === (d.localidade || '').toLowerCase() && c.uf.toLowerCase() === (d.uf || '').toLowerCase()
      );
      if (cidadeMatch) {
        setForm(v => ({ ...v, cidadeId: cidadeMatch.id }));
      }
    } catch (err: any) {
      setCepErro(err.message === 'CEP não encontrado' ? err.message : 'Erro ao buscar CEP.');
    }
    finally { setBuscandoCep(false); }
  };

  return (
    <Fragment>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left pe-0">
            <div className="card shadow-xss w-100 border-0 p-4 mb-3 d-flex flex-row align-items-center justify-content-between">
              <div>
                <h2 className="fw-700 mb-1 font-md text-grey-900"><i className="bi bi-grid-3x3-gap text-primary me-2"></i>Gestão de Quadras</h2>
                <p className="font-xssss text-grey-500 mb-0">Gerencie locais, quadras, horários e preços</p>
              </div>
              {isAdmin && (
                <div className="d-flex align-items-center gap-2">
                  <button className="btn btn-light rounded-xl font-xssss fw-600 px-3" data-tooltip="Configurar taxa de conveniência" data-tooltip-pos="bottom" onClick={() => navigate('/admin/quadras/taxa-conveniencia')}><i className="bi bi-percent me-1"></i> Taxa de conveniência</button>
                  <button className="btn bg-primary-gradiant text-white rounded-xl font-xssss fw-600 px-3" onClick={abrirNovo}><i className="feather-plus me-1"></i> Novo local</button>
                </div>
              )}
            </div>

            <div className="card shadow-xss w-100 border-0 p-3 mb-3">
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <select className="form-select rounded-xl font-xsss" style={{ width: 180 }} value={filtroAluguel} onChange={e => setFiltroAluguel(e.target.value as any)}>
                  <option value="todos">Todos os locais</option>
                  <option value="habilitado">Aluguel habilitado</option>
                  <option value="desabilitado">Aluguel desabilitado</option>
                </select>
                <select className="form-select rounded-xl font-xsss" style={{ minWidth: 220, flex: '1 1 auto' }} value={filtroCidade} onChange={e => setFiltroCidade(e.target.value)}>
                  <option value="">Todas as cidades</option>
                  {cidades.map((c: any) => <option key={c.id} value={c.id}>{c.nome} — {c.uf}</option>)}
                </select>
                <div className="position-relative flex-grow-1" style={{ minWidth: 180 }}>
                  <i className="feather-search position-absolute text-grey-400" style={{ left: 10, top: '50%', transform: 'translateY(-50%)' }}></i>
                  <input className="form-control rounded-xl font-xsss ps-5" placeholder="Buscar por nome..." value={filtroDescricao} onChange={e => setFiltroDescricao(e.target.value)} />
                </div>
                {(filtroAluguel !== 'habilitado' || filtroCidade || filtroDescricao) && (
                  <button className="btn btn-light rounded-xl font-xssss px-3" onClick={() => { setFiltroAluguel('habilitado'); setFiltroCidade(''); setFiltroDescricao(''); }}><i className="feather-x me-1"></i>Limpar</button>
                )}
              </div>
            </div>

            {loading && <Load />}
            {error && <div className="alert alert-danger font-xsss">{error.message}</div>}
            {!loading && locais.length === 0 && (
              <div className="card shadow-xss w-100 p-5 text-center border-0">
                <i className="bi bi-buildings font-xxl text-grey-300 d-block mb-2"></i>
                <p className="text-grey-500 font-xss mb-2">Nenhum local cadastrado.</p>
                {isAdmin && <button className="btn bg-primary-gradiant text-white rounded-xl font-xssss fw-600 px-3 mx-auto" onClick={abrirNovo}><i className="feather-plus me-1"></i> Cadastrar primeiro local</button>}
              </div>
            )}
            {locais.map((local: any) => (
              <LocalCard key={local.id} local={local} onEditar={abrirEditar} onDeletarLocal={handleDeletarLocal} onAdmins={setLocalAdmins} onBloqueio={setLocalBloqueio} onDesconto={setLocalDesconto} isAdmin={isAdmin} />
            ))}
          </div>
        </div>
      </div>

      {modalForm && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', position: 'fixed', inset: 0, zIndex: 9999, overflowY: 'auto' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 520 }}>
            <div className="modal-content rounded-xxl border-0 shadow-lg p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="fw-700 font-xss mb-0">{localEditando ? 'Editar Local' : 'Novo Local'}</h5>
                <button className="btn-close" onClick={fecharForm} />
              </div>
              <form onSubmit={handleSubmit}>
                <div className="mb-2"><label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Nome *</label><input className="form-control rounded-xl font-xsss" value={form.nome} onChange={f('nome')} required /></div>
                <div className="mb-2"><label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Descrição</label><textarea className="form-control rounded-xl font-xsss" rows={2} value={form.descricao} onChange={f('descricao')} /></div>
                <div className="mb-3">
                  <LocalizacaoMapaEditor
                    urlLocalizacao={form.urlLocalizacao}
                    onChange={(url) => setForm(v => ({ ...v, urlLocalizacao: url }))}
                  />
                </div>
                <p className="font-xssss fw-700 text-grey-700 mb-2">Endereço</p>
                <div className="row mb-2">
                  <div className="col-5">
                    <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">CEP</label>
                    <div className="d-flex gap-1">
                      <input className="form-control rounded-xl font-xsss" value={form.cep} onChange={e => { setCepErro(null); setForm(v => ({ ...v, cep: e.target.value })); }} placeholder="00000-000" />
                      <button type="button" className="btn btn-light btn-sm rounded-xl" onClick={buscarCep} disabled={buscandoCep} title="Buscar endereço pelo CEP">
                        {buscandoCep ? <i className="bi bi-hourglass-split"></i> : <i className="bi bi-search"></i>}
                      </button>
                    </div>
                    {cepErro && <small className="text-danger font-xssss">{cepErro}</small>}
                  </div>
                  <div className="col-7"><label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Bairro</label><input className="form-control rounded-xl font-xsss" value={form.bairro} onChange={f('bairro')} /></div>
                </div>
                <div className="row mb-2">
                  <div className="col-8"><label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Logradouro</label><input className="form-control rounded-xl font-xsss" value={form.logradouro} onChange={f('logradouro')} /></div>
                  <div className="col-4"><label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Número</label><input className="form-control rounded-xl font-xsss" value={form.numero} onChange={f('numero')} /></div>
                </div>
                <div className="mb-2"><label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Complemento</label><input className="form-control rounded-xl font-xsss" value={form.complemento} onChange={f('complemento')} /></div>
                <div className="mb-3">
                  <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Cidade</label>
                  <select className="form-select rounded-xl font-xsss" value={form.cidadeId} onChange={f('cidadeId')}>
                    <option value="">Selecione...</option>
                    {cidades.map((c: any) => <option key={c.id} value={c.id}>{c.nome} — {c.uf}</option>)}
                  </select>
                </div>
                {erro && <div className="alert alert-danger font-xssss py-2 mb-2">{erro}</div>}
                <p className="font-xssss fw-700 text-grey-700 mb-2 mt-3">Meios de Pagamento</p>
                <div className="d-flex gap-4 mb-3">
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="aceitaPix" checked={form.aceitaPix} onChange={e => setForm(v => ({ ...v, aceitaPix: e.target.checked }))} />
                    <label className="form-check-label font-xssss fw-600 text-grey-700" htmlFor="aceitaPix">Aceita PIX</label>
                  </div>
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="aceitaCartao" checked={form.aceitaCartao} onChange={e => setForm(v => ({ ...v, aceitaCartao: e.target.checked }))} />
                    <label className="form-check-label font-xssss fw-600 text-grey-700" htmlFor="aceitaCartao">Aceita Cartão</label>
                  </div>
                </div>
                {isAdmin && (
                  <>
                    <p className="font-xssss fw-700 text-grey-700 mb-1 mt-3">Liga recebedora</p>
                    <p className="font-xssss text-grey-500 mb-2">Define para qual liga vai o repasse (split) dos aluguéis deste local. Se nenhuma for definida, o recebimento vai integralmente para a InPlay. Apenas o administrador geral pode alterar.</p>
                    <div className="mb-3">
                      <LigaRecebedoraInput
                        value={form.ligaRecebedoraId}
                        nomeSelecionado={form.ligaRecebedoraNome}
                        onSelecionar={(ligaId, ligaNome) => setForm(v => ({ ...v, ligaRecebedoraId: ligaId, ligaRecebedoraNome: ligaNome }))}
                        onLimpar={() => setForm(v => ({ ...v, ligaRecebedoraId: '', ligaRecebedoraNome: '' }))}
                      />
                    </div>
                  </>
                )}
                <div className="d-flex gap-2">
                  <button type="submit" className="btn bg-primary-gradiant text-white rounded-xl font-xssss fw-600 px-3" disabled={criando || atualizando}>{criando || atualizando ? 'Salvando...' : 'Salvar'}</button>
                  <button type="button" className="btn btn-light rounded-xl font-xssss fw-600 px-3" onClick={fecharForm}>Cancelar</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {localAdmins && <ModalAdmins local={localAdmins} onFechar={() => setLocalAdmins(null)} />}
      {localBloqueio && <ModalBloqueioLote local={localBloqueio} onFechar={() => setLocalBloqueio(null)} />}
      {localDesconto && <ModalDescontoAluno local={localDesconto} onFechar={() => setLocalDesconto(null)} />}

      <Appfooter />
    </Fragment>
  );
};

export default AdminQuadrasDashboard;
